import type { InsightCard, InsightSeverity, RatioAssessment, RatioUnit } from '../../../data/ServerApiClient'

// Shared, presentation-only formatters for the calm CFO pages. Numbers come
// from the canonical server engines; these only format them.

export const trimStr = (value: unknown): string => String(value ?? '').trim()

let activeCurrencyCode = 'USD'

/**
 * The CFO route mounts one workbook at a time. Configure its native base
 * currency before rendering the shared provider-neutral pages so every
 * existing formatter keeps the same API while producing the correct symbol.
 */
export const configureCfoCurrency = (currencyCode: unknown): void => {
  const normalized = trimStr(currencyCode).toUpperCase()
  if (/^[A-Z]{3}$/.test(normalized)) activeCurrencyCode = normalized
}

export const cfoCurrencyCode = (): string => activeCurrencyCode

export type CfoCurrencyDisplay = 'qualified' | 'scoped'

const normalizedCurrencyCode = (currencyCode: unknown): string => {
  const normalized = trimStr(currencyCode).toUpperCase()
  return /^[A-Z]{3}$/.test(normalized) ? normalized : activeCurrencyCode
}

export const cfoCurrencySymbol = (
  currencyCode: unknown = activeCurrencyCode,
  display: CfoCurrencyDisplay = 'qualified',
): string => {
  const normalized = normalizedCurrencyCode(currencyCode)
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: normalized,
      // A report-level currency note makes a repeated regional prefix noisy.
      // Mixed-currency collections keep the qualified symbol on every value.
      currencyDisplay: display === 'scoped' ? 'narrowSymbol' : 'symbol',
    }).formatToParts(0).find((part) => part.type === 'currency')?.value || '$'
  } catch {
    return display === 'scoped' && normalized.endsWith('D')
      ? '$'
      : normalized === 'USD'
        ? '$'
        : `${normalized} `
  }
}

export type CfoCurrencyFormatOptions = {
  currencyCode?: string | null
  /**
   * `scoped` is for a report whose title/subtitle identifies its sole currency.
   * `qualified` is for standalone or mixed-currency values.
   */
  display?: CfoCurrencyDisplay
}

export const formatCurrencyCompact = (
  value: number | null | undefined,
  options: CfoCurrencyFormatOptions = {},
): string => {
  if (value == null || !Number.isFinite(value)) return '—'
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  const symbol = cfoCurrencySymbol(
    options.currencyCode ?? activeCurrencyCode,
    options.display ?? 'qualified',
  )
  if (abs >= 1_000_000_000) return `${sign}${symbol}${(abs / 1_000_000_000).toFixed(1)}B`
  if (abs >= 1_000_000) return `${sign}${symbol}${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}${symbol}${(abs / 1_000).toFixed(1)}K`
  return `${sign}${symbol}${abs.toFixed(0)}`
}

export type CfoCurrencyPresentation = {
  currencyCodes: string[]
  display: CfoCurrencyDisplay
  note: string
}

/**
 * Decide once per table/report whether currency belongs in the heading or on
 * each value. Callers with mixed source currencies should pass the currency
 * code alongside each value to `formatCurrencyCompact`.
 */
export const cfoCurrencyPresentation = (
  currencyCodes: Array<string | null | undefined> = [activeCurrencyCode],
): CfoCurrencyPresentation => {
  const normalized = Array.from(
    new Set(
      currencyCodes
        .map((code) => trimStr(code).toUpperCase())
        .filter((code) => /^[A-Z]{3}$/.test(code)),
    ),
  ).sort()
  const codes = normalized.length ? normalized : [activeCurrencyCode]
  if (codes.length === 1) {
    return {
      currencyCodes: codes,
      display: 'scoped',
      note: `all amounts in ${codes[0]}`,
    }
  }
  return {
    currencyCodes: codes,
    display: 'qualified',
    note: 'mixed currencies · denomination shown per value',
  }
}

// Whole-dollar, fully separated. Use wherever a compact rendering would make two
// genuinely different numbers look identical — e.g. "$999.9K vs $1.0M" for a real
// $5K gap, which reads as a rounding glitch and destroys trust in the message.
export const formatCurrencyExact = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—'
  const sign = value < 0 ? '-' : ''
  return `${sign}${cfoCurrencySymbol()}${Math.round(Math.abs(value)).toLocaleString('en-US')}`
}

export const formatSignedCurrency = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—'
  const sign = value > 0 ? '+' : value < 0 ? '\u2212' : ''
  return `${sign}${formatCurrencyCompact(Math.abs(value))}`
}

export const normalizeCurrencyTextCompact = (text: string | null | undefined): string => {
  const normalizedText = trimStr(text)
  return normalizedText.replace(
    /(^|[\s([{])([+\-\u2212])?\$([0-9]+(?:,[0-9]{3})*|[0-9]+)(?:\.([0-9]+))?([KMB])?\b/gi,
    (_match, prefix: string, rawSign: string | undefined, rawWhole: string, rawFraction: string | undefined, rawSuffix: string | undefined) => {
      const numeric = Number(`${rawWhole.replace(/,/g, '')}${rawFraction ? `.${rawFraction}` : ''}`)
      const multiplier = rawSuffix?.toUpperCase() === 'B'
        ? 1_000_000_000
        : rawSuffix?.toUpperCase() === 'M'
          ? 1_000_000
          : rawSuffix?.toUpperCase() === 'K'
            ? 1_000
            : 1
      const amount = numeric * multiplier
      if (!Number.isFinite(amount)) return _match
      const sign = rawSign === '+' ? '+' : rawSign === '-' || rawSign === '\u2212' ? '-' : ''
      return `${prefix}${sign}${formatCurrencyCompact(amount)}`
    },
  )
}

/** `value` is a growth fraction (0.12 → +12.0%). */
export const formatSignedPct = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—'
  const sign = value > 0 ? '+' : value < 0 ? '\u2212' : ''
  return `${sign}${Math.abs(value * 100).toFixed(1)}%`
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

export const formatAsOf = (dateStr: string | null | undefined): string => {
  const s = trimStr(dateStr)
  if (!s) return '—'
  const m = /^(\d{4})-(\d{2})/.exec(s)
  if (!m) return s
  const year = Number(m[1])
  const month = Number(m[2])
  if (!month || month < 1 || month > 12) return s
  return `${MONTHS[month - 1]} ${year}`
}

// Insight severity → calm card class (CSS in CfoPageShell.css: good/watch/check/bad).
export const severityCardClass = (sev: InsightSeverity): string => sev

export const SEVERITY_LABEL: Record<InsightSeverity, string> = {
  good: "You're fine",
  check: 'Verify',
  watch: 'Keep an eye',
  bad: 'Needs attention',
}

export const formatImpact = (card: InsightCard): string => {
  const { value, unit } = card.impact
  if (!Number.isFinite(value)) return '—'
  const sign = value > 0 ? '+' : value < 0 ? '\u2212' : ''
  const abs = Math.abs(value)
  switch (unit) {
    case 'usd':
      return `${sign}${formatCurrencyCompact(abs)}`
    case 'pct':
      return `${sign}${abs.toFixed(1)}%`
    case 'pct_pts':
      return `${sign}${abs.toFixed(1)} pts`
    case 'months':
      return `~${abs.toFixed(0)} mo`
    case 'ratio':
      return `${sign}${abs.toFixed(2)}`
    default:
      return `${sign}${abs}`
  }
}

// Magnitude beyond which a ratio value is "off the scale". A denominator hovering near zero yields an
// unbounded quotient (a 247x coverage, a 12,000% margin) that is noise, not signal — so we clamp the
// DISPLAY to a marker (">100x") rather than show false precision. The server suppresses the common
// degenerate cases at the source (near-zero/negative denominators; see `ratio_denominator_is_material`
// in ml_understanding.rs); this is the universal display backstop for anything that still computes to
// an extreme magnitude. Mirrors `ratio_value_out_of_range_marker` in fpa.rs. Currency has no cap — any
// dollar amount is a meaningful figure.
const RATIO_DISPLAY_CAP: Record<RatioUnit, number> = {
  times: 100, // >100x coverage/turnover ⇒ denominator ≈ 0
  ratio: 50, // fraction 50 = 5,000%
  days: 3650, // 10 years
  currency: Number.POSITIVE_INFINITY,
}

/** Whether a ratio value is beyond the meaningful display range for its unit (an off-the-scale figure
 * a near-zero denominator produced). Non-finite/null is handled separately as "—". */
export const isRatioValueOutOfRange = (value: number | null | undefined, unit: RatioUnit): boolean =>
  value != null && Number.isFinite(value) && Math.abs(value) > RATIO_DISPLAY_CAP[unit]

/**
 * Format a ratio value for display by its unit. Shared by the Ratios page, the
 * guided-analysis ratio-health gauge, and grounding payloads so a ratio renders
 * identically everywhere (e.g. current ratio "1.80x", DSO "45d", debt-to-assets
 * as a "%" of assets). Off-the-scale magnitudes clamp to a ">100x" marker instead
 * of printing false precision (see RATIO_DISPLAY_CAP).
 */
export const formatRatioValue = (value: number | null | undefined, unit: RatioUnit): string => {
  if (value == null || !Number.isFinite(value)) return '—'
  if (isRatioValueOutOfRange(value, unit)) {
    const cap = RATIO_DISPLAY_CAP[unit]
    const over = value > 0 ? '>' : '<−' // ">" or "<−"
    switch (unit) {
      case 'times':
        return `${over}${cap}x`
      case 'ratio':
        return `${over}${(cap * 100).toLocaleString()}%`
      case 'days':
        return `${over}${cap.toLocaleString()}d`
      default:
        break
    }
  }
  switch (unit) {
    case 'times':
      return `${value.toFixed(2)}x`
    case 'ratio':
      return `${(value * 100).toFixed(1)}%`
    case 'days':
      return `${value.toFixed(0)}d`
    case 'currency': {
      // Sign-aware compaction: negatives compact too (-$14.9K, not $-14916).
      const sign = value < 0 ? '-' : ''
      const abs = Math.abs(value)
      const symbol = cfoCurrencySymbol()
      if (abs >= 1_000_000) return `${sign}${symbol}${(abs / 1_000_000).toFixed(1)}M`
      return abs >= 1000 ? `${sign}${symbol}${(abs / 1000).toFixed(1)}K` : `${sign}${symbol}${abs.toFixed(0)}`
    }
    default:
      return String(value)
  }
}

/** Tone (label + theme-aware colors) for a ratio health assessment. */
export type RatioTone = { label: string; color: string; bg: string; edge: string }

export const ratioToneForAssessment = (assessment: RatioAssessment): RatioTone => {
  switch (assessment) {
    case 'concern':
      return { label: 'Needs attention', color: 'var(--neg)', bg: '#fdecea', edge: 'var(--neg)' }
    case 'watch':
      return { label: 'Keep an eye', color: 'var(--watch-acc)', bg: '#fbf3e2', edge: 'var(--watch-acc)' }
    case 'healthy':
      return { label: 'Healthy', color: 'var(--good-acc)', bg: '#e8f6ef', edge: 'var(--good-acc)' }
    default:
      return { label: 'Needs context', color: 'var(--muted)', bg: 'var(--wash)', edge: 'var(--hair)' }
  }
}
